import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
});

export default function Name() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const nameInput = useRef<any>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: communityMembership ? communityMembership.profile.name : '',
    }
  });

  const [name] = watch(["name"]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "Name.onSubmit",
        "/rabbl/api/set_name",
        "POST",
        {
          domain: domain,
          name: data.name,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      if (edit && edit.length > 0 && router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Name.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (nameInput && nameInput.current) {
        nameInput.current?.focus();
      }
    }
  }, [nameInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Your name"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Name on your profile:</AppText>

          <Controller
            control={control}
            name="name"
            rules={{
              validate: (x) => {
                if (x == '') {
                  return 'Your name is required.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <TextInput
                  ref={nameInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Name"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  autoComplete="nickname"
                  textContentType="nickname"
                />
              </View>
            )}
          />
          {errors.name && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.name.message} ^</AppText>}

          <View style={styles.buttonWrapper}>
            <Button95
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              disabled={name == ''}
            >{ edit ? "Save" : "Next" }</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
